import { ProviderCard, ProviderLogo } from '@/features/account-calendar/provider-card'
import { type CalendarProvider } from '@jobilla/nexus-sdk'
import { Button } from '@jobilla/starfield'
import { useTranslation } from 'react-i18next'

/** Brand marks per provider, until we embed the real logos. */
const PROVIDER_LOGOS: Record<CalendarProvider, { letter: string; className: string }> = {
	google: {
		letter: 'G',
		className: 'bg-[#1a73e8]',
	},
	microsoft_365: {
		letter: 'M',
		className: 'bg-[#0078d4]',
	},
}

/**
 * One provider row on the calendar settings page. A user has at most one calendar
 * connected, so each row is in exactly one state:
 *  - `connected`  — this provider is the connected one (offer disconnect / reconnect)
 *  - `locked`     — the *other* provider is connected (offer nothing; explain why)
 *  - `available`  — nothing connected (offer connect + the scope disclosure)
 *  - `unavailable` — the provider isn't offered yet (greyed out, "Coming soon")
 */
export function CalendarConnectionCard(
	{
		provider,
		state,
		email,
		needsReconnect = false,
		lockedByName,
		connectingProvider,
		isDisconnecting,
		onConnect,
		onDisconnect,
	}: {
		provider: CalendarProvider
		state: 'connected' | 'locked' | 'available' | 'unavailable'
		email?: string
		needsReconnect?: boolean
		lockedByName?: string
		connectingProvider: CalendarProvider | null
		isDisconnecting: boolean
		onConnect: (provider: CalendarProvider) => void
		onDisconnect: () => void
	},
) {
	const { t } = useTranslation('common')
	const logo = PROVIDER_LOGOS[provider]
	const name = t(`account.calendar.providers.${provider}`)
	const isConnecting = connectingProvider === provider
	// Any connect or disconnect in flight disables every action here, so a second
	// click can't start a competing OAuth handoff or disconnect mid-operation.
	const isBusy = connectingProvider !== null || isDisconnecting

	const statusLine = state === 'connected'
		? needsReconnect
			? t('account.calendar.reconnect_needed_as', { email })
			: t('account.calendar.connected_as', { email })
		: undefined

	return (
		<ProviderCard
			provider={provider}
			name={name}
			logo={<ProviderLogo letter={logo.letter} className={logo.className} />}
			statusLine={statusLine}
			disabled={state === 'locked' || state === 'unavailable'}
			badge={state === 'unavailable'
				? (
					<span className="rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium text-grey-500">
						{t('account.calendar.coming_soon')}
					</span>
				)
				: undefined}
		>
			{state === 'connected'
				? (
					<div className="mt-3 flex flex-col gap-3">
						{needsReconnect
							? (
								<div>
									<Button
										type="primary"
										scale="small"
										htmlButtonType="button"
										disabled={isBusy}
										onClick={() => onConnect(provider)}
									>
										{isConnecting
											? t('account.calendar.connecting')
											: t('account.calendar.reconnect')}
									</Button>
								</div>
							)
							: null}
						<div>
							<Button
								type="tertiary"
								scale="small"
								htmlButtonType="button"
								disabled={isBusy}
								onClick={onDisconnect}
							>
								{isDisconnecting
									? t('account.calendar.disconnecting')
									: t('account.calendar.disconnect')}
							</Button>
						</div>
					</div>
				)
				: null}

			{state === 'locked'
				? (
					<p className="mt-2 text-sm text-grey-500">
						{t('account.calendar.locked', { provider: lockedByName })}
					</p>
				)
				: null}

			{state === 'available'
				? (
					<div className="mt-3">
						<Button
							type="primary"
							scale="small"
							htmlButtonType="button"
							disabled={isBusy}
							onClick={() => onConnect(provider)}
						>
							{isConnecting
								? t('account.calendar.connecting')
								: t('account.calendar.connect', { provider: name })}
						</Button>
					</div>
				)
				: null}

			{provider == 'google'
				? (
					<p className="mt-3 text-sm text-grey-600">
						{t('account.calendar.disclosure.limited_use')}{' '}
						<a
							href="https://developers.google.com/terms/api-services-user-data-policy#limited-use"
							target="_blank"
							rel="noreferrer"
							className="text-brand-600 underline"
						>
							{t('account.calendar.disclosure.limited_use_cta')}
						</a>
					</p>
				)
				: null}
		</ProviderCard>
	)
}
